import { motion } from 'framer-motion'
import { Button } from '@mui/material'
import { Link } from 'react-router-dom'
import { colors } from '../styles/theme'

const AnimatedHero = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10,
      },
    },
  }

  return (
    <div className="relative min-h-[600px] flex items-center justify-center overflow-hidden animated-gradient noise-overlay">
      {/* Animated particles/dots - Terminal style */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-sm"
            style={{
              width: Math.random() > 0.7 ? '3px' : '2px',
              height: Math.random() > 0.7 ? '3px' : '2px',
              backgroundColor: i % 3 === 0 ? '#00ff9f' : i % 3 === 1 ? '#00d4ff' : '#00ff41',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              boxShadow: `0 0 ${Math.random() > 0.5 ? '10px' : '5px'} currentColor`,
            }}
            animate={{
              y: [0, -40, 0],
              opacity: [0.1, 0.6, 0.1],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 3 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <motion.div
        className="container-custom relative z-10 text-center"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants} className="mb-6">
          <h1 className="text-5xl md:text-7xl font-bold text-off-white mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff9f] to-[#7c3aed]">Prompted Thoughts</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 font-mono">
            Where prompts meet production
          </p>
        </motion.div>

        <motion.p
          variants={itemVariants}
          className="text-lg md:text-xl text-gray-400 mb-8 max-w-2xl mx-auto"
        >
          A developer-centric podcast at the intersection of Generative AI, app development,
          developer experience, and data.
        </motion.p>

        {/* Value props pills */}
        <motion.div
          variants={itemVariants}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {['Gen AI', 'App Dev', 'DX', 'Data'].map((topic, i) => (
            <motion.div
              key={topic}
              className="pill"
              whileHover={{ scale: 1.05, y: -2 }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
            >
              {topic}
            </motion.div>
          ))}
        </motion.div>

        {/* CTAs */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            component={Link}
            to="/episodes"
            variant="contained"
            size="large"
            sx={{
              backgroundColor: colors.accent,
              color: colors.textOnAccent,
              fontWeight: 700,
              fontFamily: 'JetBrains Mono, monospace',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              boxShadow: 'var(--shadow-accent-glow)',
              '&:hover': {
                backgroundColor: colors.accentHover,
                transform: 'translateY(-2px)',
                boxShadow: 'var(--shadow-accent-glow-lg)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Browse Episodes
          </Button>
          <Button
            component={Link}
            to="/subscribe"
            variant="outlined"
            size="large"
            sx={{
              borderColor: colors.info,
              color: colors.info,
              fontWeight: 700,
              fontFamily: 'JetBrains Mono, monospace',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              borderWidth: 2,
              boxShadow: '0 0 8px rgba(var(--color-accent-info-rgb), 0.2)',
              '&:hover': {
                borderColor: colors.info,
                backgroundColor: colors.info,
                color: colors.textOnAccent,
                borderWidth: 2,
                transform: 'translateY(-2px)',
                boxShadow: '0 0 12px rgba(var(--color-accent-info-rgb), 0.3)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Subscribe
          </Button>
        </motion.div>
      </motion.div>
    </div>
  )
}

export default AnimatedHero
